import { useState } from 'react';
import { Button, Form, InputNumber, Radio } from 'antd';
import type { FormInstance } from 'antd';
import {
  volumeBonusFor,
  volumeBonusTierBreakdown,
  type VolumeBonusTier,
} from '@salaries/shared';

// בונוס מדרגות — the marginal ₪-per-class ladder. Editor for the tier list plus
// (at box level) the on/off radio, shared by the box defaults and the per-staff
// "custom" section so the inputs stay identical.
//
// MARGINAL, like income-tax brackets: each tier runs from its own "משיעור" up to
// the class before the next tier's, and every class pays the rate of the bracket
// it falls in. A tier the coach never reaches simply pays nothing; the ones below
// it still pay. The little calculator at the bottom spells that out with real
// numbers, because "30 שיעורים → ₪20 לשיעור" reads two different ways until you
// see 50 classes come out as 30×20 + 20×30.

/** The range label for a tier, from the ladder it lives in: "שיעורים 1–30" /
 *  "משיעור 31 ואילך". Computed off the SORTED ladder so the rows can stay in
 *  whatever order they were typed. */
function rangeLabels(tiers: VolumeBonusTier[]): Map<number, string> {
  const map = new Map<number, string>();
  for (const b of volumeBonusTierBreakdown(0, tiers)) {
    map.set(
      b.from_class,
      b.to_class == null ? `משיעור ${b.from_class} ואילך` : `שיעורים ${b.from_class}–${b.to_class}`,
    );
  }
  return map;
}

export function VolumeTiersInput({
  value = [],
  onChange,
}: {
  value?: VolumeBonusTier[];
  onChange?: (v: VolumeBonusTier[]) => void;
}) {
  const rows = value ?? [];
  const commit = (next: VolumeBonusTier[]) => onChange?.(next);
  const patch = (i: number, p: Partial<VolumeBonusTier>) =>
    commit(rows.map((r, idx) => (idx === i ? { ...r, ...p } : r)));
  const remove = (i: number) => commit(rows.filter((_, idx) => idx !== i));
  const add = () => {
    // Start the new tier one class above the highest one so far — the common case
    // is "…and from there on up".
    const nextFrom = rows.length ? Math.max(...rows.map((r) => r.from_class || 1)) + 1 : 1;
    commit([...rows, { from_class: nextFrom, amount: 0 }]);
  };

  const labels = rangeLabels(rows);
  // Two rows starting at the same class: only one of them can survive the save
  // (the last one entered wins), so say so instead of silently dropping money.
  const dup = rows.length !== new Set(rows.map((r) => r.from_class)).size;

  // Sample count for the calculator — seeded above the top tier so it shows a
  // full ladder by default.
  const topFrom = rows.length ? Math.max(...rows.map((r) => r.from_class || 1)) : 0;
  const [sample, setSample] = useState<number | null>(null);
  const sampleCount = sample ?? topFrom + 20;
  const samplePay = volumeBonusFor(sampleCount, { enabled: true, tiers: rows });
  const sampleParts = volumeBonusTierBreakdown(sampleCount, rows).filter((b) => b.classes > 0);

  return (
    <div>
      {rows.length > 0 && (
        <div className="border rounded mb-2">
          {rows.map((r, i) => (
            <div
              key={i}
              className="flex items-center gap-2 px-3 py-2 border-b last:border-b-0 flex-wrap"
            >
              <span className="text-secondary-text">משיעור</span>
              <InputNumber
                min={1}
                max={10000}
                value={r.from_class}
                onChange={(v) => patch(i, { from_class: Math.max(1, Math.floor(Number(v) || 1)) })}
                dir="ltr"
                style={{ width: 80 }}
              />
              <InputNumber
                min={0}
                suffix="₪"
                value={r.amount}
                onChange={(v) => patch(i, { amount: Math.max(0, Number(v) || 0) })}
                dir="ltr"
                style={{ width: 110 }}
              />
              <span className="text-secondary-text">לשיעור</span>
              <span className="text-small text-secondary-text">({labels.get(r.from_class)})</span>
              <span className="flex-1" />
              <Button size="small" danger type="text" onClick={() => remove(i)}>
                הסר
              </Button>
            </div>
          ))}
        </div>
      )}

      <Button onClick={add} disabled={rows.length >= 20}>
        הוסף מדרגה
      </Button>

      {dup && (
        <div className="text-small mt-2" style={{ color: '#d4380d' }}>
          שתי מדרגות מתחילות מאותו מספר שיעורים — שימו לב כי רק האחרונה תישמר.
        </div>
      )}

      {rows.length > 0 && (
        <div className="mt-3 p-3 rounded bg-grey-bg">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-secondary-text">בדיקה: איש צוות שאימן</span>
            <InputNumber
              min={0}
              max={10000}
              value={sampleCount}
              onChange={(v) => setSample(Math.max(0, Math.floor(Number(v) || 0)))}
              dir="ltr"
              style={{ width: 90 }}
            />
            <span className="text-secondary-text">שיעורים יקבל</span>
            <span className="font-semibold">₪{samplePay.toLocaleString()}</span>
          </div>
          {sampleParts.length > 0 && (
            <div className="text-small text-secondary-text mt-1">
              {sampleParts
                .map((b) => `${b.classes}×${b.amount}₪`)
                .join(' + ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// showEnabled (box defaults): the on/off radio is offered. In the per-staff drawer
// (showEnabled=false) the source select already drives on/off, so only the ladder
// shows and the consumer forces enabled=true on save.
export function VolumeBonusFields({
  form,
  showEnabled = true,
}: {
  form: FormInstance;
  showEnabled?: boolean;
}) {
  const enabled = Form.useWatch('volume_bonus_enabled', form);
  // The ladder field stays MOUNTED even while the bonus is off, so turning it
  // back on doesn't come back to an empty ladder (and antd doesn't drop it on save).
  const tiers = (
    <div style={{ display: showEnabled && !enabled ? 'none' : undefined }} className="mt-2">
      <Form.Item name="volume_bonus_tiers" className="mb-0">
        <VolumeTiersInput />
      </Form.Item>
    </div>
  );
  if (!showEnabled) return tiers;
  return (
    <>
      <Form.Item name="volume_bonus_enabled" className="mb-0">
        <Radio.Group className="flex flex-col gap-3">
          <Radio value={false}>ללא בונוס לפי כמות שיעורים</Radio>
          <Radio value={true}>בונוס לפי כמות השיעורים שהועברו החודש</Radio>
        </Radio.Group>
      </Form.Item>
      {tiers}
    </>
  );
}
